import React, { useState, useEffect, useRef } from 'react';
import styles from './BookIllustration.module.css';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import Image1 from '../../images/Page64_page-0001.jpg';
import Image2 from '../../images/Elephant-Ant-Story-Final.jpg';
import Image3 from '../../images/The Sky Model-2_page-0001.jpg';

const BookGallery = () => {
  const [activeBook, setActiveBook] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [ripples, setRipples] = useState([]);
  const [stars, setStars] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('graphic');
  const canvasRef = useRef(null);

  // Book illustrations data
  const books = [
    {
      id: 1,
      title: "The Enchanted Forest",
      image: Image1,
    },
    {
      id: 2,
      title: "Ocean Dreams",
      image: Image2,
    },
    {
      id: 3,
      title: "Starlight Tales",
      image: Image3,
    },

  ];

  // Scroll handler for header
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize stars
  useEffect(() => {
    const newStars = Array.from({ length: 150 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3,
      speed: Math.random() * 0.5 + 0.2,
      opacity: Math.random()
    }));
    setStars(newStars);
  }, []);

  // Animate stars
  useEffect(() => {
    const interval = setInterval(() => {
      setStars(prev => prev.map(star => ({
        ...star,
        opacity: Math.sin(Date.now() * 0.001 * star.speed + star.id) * 0.5 + 0.5
      })));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Track mouse for interactive effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };

    const handleClick = (e) => {
      const ripple = {
        id: Date.now(),
        x: e.clientX,
        y: e.clientY
      };
      setRipples(prev => [...prev, ripple]);
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== ripple.id));
      }, 1500);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, []);

  // Canvas animation for aurora effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    let time = 0;
    const waves = [];
    
    for (let i = 0; i < 3; i++) {
      waves.push({
        y: canvas.height * (0.3 + i * 0.2),
        length: 0.01 + i * 0.003,
        amplitude: 40 + i * 20,
        frequency: 0.01 + i * 0.005,
        color: ['rgba(138, 43, 226, 0.1)', 'rgba(30, 144, 255, 0.1)', 'rgba(255, 105, 180, 0.1)'][i]
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      waves.forEach(wave => {
        ctx.beginPath();
        ctx.moveTo(0, wave.y);
        
        for (let x = 0; x < canvas.width; x++) {
          const y = wave.y + Math.sin(x * wave.length + time * wave.frequency) * wave.amplitude;
          ctx.lineTo(x, y);
        }
        
        ctx.lineTo(canvas.width, canvas.height);
        ctx.lineTo(0, canvas.height);
        ctx.closePath();
        ctx.fillStyle = wave.color;
        ctx.fill();
      });
      
      time += 0.05;
      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.pageWrapper}>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      
      <div className="gallery-container">
      {/* Multi-layer Interactive Background */}
      <canvas ref={canvasRef} className="aurora-canvas" />
      
      {/* Animated gradient orbs */}
      <div className="orb-container">
        <div 
          className="orb orb-1"
          style={{
            transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`
          }}
        />
        <div 
          className="orb orb-2"
          style={{
            transform: `translate(${-mousePosition.x * 0.2}px, ${mousePosition.y * 0.4}px)`
          }}
        />
        <div 
          className="orb orb-3"
          style={{
            transform: `translate(${mousePosition.x * 0.15}px, ${-mousePosition.y * 0.25}px)`
          }}
        />
      </div>

      {/* Starfield */}
      <div className="starfield">
        {stars.map(star => (
          <div
            key={star.id}
            className="star"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity
            }}
          />
        ))}
      </div>

      {/* Interactive ripples on click */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="ripple"
          style={{
            left: ripple.x,
            top: ripple.y
          }}
        />
      ))}





      {/* Mouse follower light */}
      <div 
        className="mouse-light"
        style={{
          left: `${mousePosition.x}%`,
          top: `${mousePosition.y}%`
        }}
      />

      {/* Header */}
      <header className="gallery-header">
        <h1 className="main-title">
          <span className="title-word">Illustrated</span>
          <span className="title-word">Stories</span>
        </h1>
        <p className="subtitle">A Collection of Timeless Tales</p>
      </header>

      {/* Gallery Grid */}
      <div className="gallery-grid">
        {books.map((book, index) => (
          <div
            key={book.id}
            className={`book-card ${activeBook === book.id ? 'active' : ''}`}
            style={{ animationDelay: `${index * 0.1}s` }}
            onMouseEnter={() => setActiveBook(book.id)}
            onMouseLeave={() => setActiveBook(null)}
          >
            <div className="book-image-wrapper">
              <img 
                src={book.image} 
                alt={book.title}
                className="book-image"
              />
              <div 
                className="book-overlay"
                style={{ backgroundColor: book.color }}
              />
              <div className="book-shine" />
            </div>
            
          </div>
        ))}
      </div>

      </div>
      
      <div className={styles.footerOverride}>
        <Footer mode={mode} background="illustrations" />
      </div>
    </div>
  );
};

export default BookGallery;